import React, { useMemo } from 'react';
import { Row, Col } from 'antd';
import AllocationDetailsTable from './AllocationDetailsTable';
import ViewershipPool from './ViewershipPool';
import { AllocationPeriod, AllocationStrategy, PaymentAllocationManagerProps, ViewershipPoolProps } from '../../../../utils/common.interface';
import { PaymentData  } from '../../types/main';
import { useViewershipPool } from '../../hooks/useViewershipPool';


const isValidAllocationPeriod = (value: any): value is AllocationPeriod => {
  return value === 'Monthly' || value === 'Quarterly';
};

const AllocationManager: React.FC<PaymentAllocationManagerProps> = ({
  unappliedPaymentData,
  setUnAppliedPaymentData,
  handleAllocate,
  selectedRecord,  
  setSelectedRecord,
  viewershipPool, 
  allocationStatus,
  getViewershipData
}) => {

  const { getGroupedData } = useViewershipPool(viewershipPool || {});
  
  
  const groupedData = useMemo(() => {
    if (!viewershipPool) {
      return null;
    }
    const result = getGroupedData(['platform', 'partner', 'channel', 'territory', 'year', 'quarter']);
    return result;
  }, [getGroupedData, viewershipPool]);


  const generateAllocationCode = (strategy: AllocationStrategy, period: AllocationPeriod): string => {
    // Base: start with partner flag (d for true, p for false)
    let code = strategy.partner ? 'd' : 'p';
    
    // Add period (m for Monthly, q for Quarterly)
    code += period === 'Monthly' ? 'm' : 'q';
    
    // Add channel and territory if selected
    if (strategy.channel) code += 'c';
    if (strategy.territory) code += 't';
    
    return code;
  };

  const handleViewershipPoolSave = (newAllocationStrategy: AllocationStrategy, newAllocationPeriod: AllocationPeriod, selectedPoolId: string) => {
    if (selectedRecord) {
      const allocationCode = generateAllocationCode(newAllocationStrategy, newAllocationPeriod);
      
      const updatedRecord: PaymentData = {  
        ...selectedRecord,
        allocationStrategy: newAllocationStrategy,
        allocationPeriod: newAllocationPeriod,
        linkedToViewership: true,
        linkedViewershipPool: selectedPoolId,
        allocationCode: allocationCode // Store the generated code
      };
      setUnAppliedPaymentData(prevDataSource => 
        prevDataSource.map(record => 
          record.key === selectedRecord.key ? updatedRecord : record
        )
      );
      setSelectedRecord(updatedRecord);
    }
  };

  const handleRecordSelection = (record: PaymentData | null) => {
    if (record) {
      const {platform, partner, channel, territory, year, quarter, month} = record;
      getViewershipData({platform, partner, years: [year], quarter});
    }
    setSelectedRecord(record);
  };
  
  if (!groupedData) {
    return <div>Loading viewership data...</div>;
  }
  

  const viewershipPoolProps: ViewershipPoolProps = {
    groupedData: groupedData || {},
    selectedRecord: selectedRecord || null,
    selectedRecordKey: selectedRecord?.key || null,
    allocationStrategy: selectedRecord?.allocationStrategy || {
      platform: true,
      partner: true,
      channel: true,
      territory: true
    },
    allocationLevel: selectedRecord?.allocationLevel || null,
    allocationPeriod: selectedRecord?.allocationPeriod && isValidAllocationPeriod(selectedRecord.allocationPeriod) 
      ? selectedRecord.allocationPeriod 
      : 'Monthly',    
    onSave: handleViewershipPoolSave,
  };

  return (
    <Row gutter={16}>
      <Col span={12}>
        <AllocationDetailsTable
          unappliedPaymentData={unappliedPaymentData}
          setUnAppliedPaymentData={setUnAppliedPaymentData}
          handleAllocate={handleAllocate}
          onRecordSelect={handleRecordSelection}
          selectedRecord={selectedRecord}
          allocationStatus={allocationStatus}
        />
      </Col>
      <Col span={12}>
       <ViewershipPool {...viewershipPoolProps} />
      </Col>
    </Row>
  );
};


export default AllocationManager;

